import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";
import Indicator from "./pages/Indicator";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import { lazy, Suspense, useEffect } from "react";
import { initTelegramWebApp } from "@/lib/telegramWebApp";
const FeedbackSubmit = lazy(() => import("./pages/FeedbackSubmit"));
const PremiumView = lazy(() => import("./pages/PremiumView"));
const Bookmarks = lazy(() => import("./pages/Bookmarks"));
const FloatingChat = lazy(() => import("./components/FloatingChat"));
const P2P = lazy(() => import("./pages/P2P"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const Connect = lazy(() => import("./pages/Connect"));
import ProtectedRoute from "./components/ProtectedRoute";
import NotFound from "./pages/NotFound";
import { BrandedConfirmProvider } from "./components/BrandedConfirmProvider";

// Renders the floating chat widget on every route EXCEPT the hidden /admin route.
const ConditionalFloatingChat = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return (
    <Suspense fallback={null}>
      <FloatingChat />
    </Suspense>
  );
};

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => { initTelegramWebApp(); }, []);

  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrandedConfirmProvider />
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/indicator" element={<Indicator />} />
            <Route path="/feedback/:token" element={<Suspense fallback={null}><FeedbackSubmit /></Suspense>} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/.lovable/oauth/consent" element={<Suspense fallback={null}><OAuthConsent /></Suspense>} />
            <Route path="/connect" element={<Suspense fallback={null}><Connect /></Suspense>} />

            {/* Protected routes (single auth gate) */}
            <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/premium/:groupId" element={<Suspense fallback={null}><PremiumView /></Suspense>} />
              <Route path="/dashboard/bookmarks" element={<Suspense fallback={null}><Bookmarks /></Suspense>} />
              <Route path="/p2p" element={<Suspense fallback={null}><P2P /></Suspense>} />
            </Route>

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <ConditionalFloatingChat />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
